/**
 * Currency utility functions for the CRM application
 * Default currency is THB (Thai Baht)
 */

/**
 * Format currency amount with proper symbol and formatting
 * @param {number} amount - The amount to format
 * @param {string} currency - The currency code (THB, USD, etc.)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currency = 'THB') => {
  if (!amount && amount !== 0) return '฿0';
  
  // Handle different currencies
  switch (currency?.toUpperCase()) {
    case 'USD':
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(amount);
    case 'THB':
    default:
      return new Intl.NumberFormat('th-TH', {
        style: 'currency',
        currency: 'THB'
      }).format(amount);
  }
};

/**
 * Get currency symbol for a given currency code
 * @param {string} currency - The currency code
 * @returns {string} Currency symbol
 */
export const getCurrencySymbol = (currency = 'THB') => {
  switch (currency?.toUpperCase()) {
    case 'USD':
      return '$';
    case 'THB':
    default:
      return '฿';
  }
};

/**
 * Format currency amount with custom symbol and no locale formatting
 * @param {number} amount - The amount to format
 * @param {string} currency - The currency code
 * @returns {string} Formatted currency string with symbol
 */
export const formatCurrencySimple = (amount, currency = 'THB') => {
  if (!amount && amount !== 0) return getCurrencySymbol(currency) + '0';
  
  const symbol = getCurrencySymbol(currency);
  return symbol + amount.toLocaleString();
};

/**
 * Parse currency string to number
 * @param {string} currencyString - The currency string to parse
 * @param {string} currency - The expected currency code
 * @returns {number|null} Parsed number or null if invalid
 */
export const parseCurrency = (currencyString, currency = 'THB') => {
  if (!currencyString) return null;
  
  // Remove currency symbols and formatting
  const cleanString = currencyString
    .replace(/[฿$€£¥,\s]/g, '')
    .replace(/บาท|baht|usd|eur|gbp|jpy/g, '')
    .trim();
  
  const parsed = parseFloat(cleanString);
  return isNaN(parsed) ? null : parsed;
};

/**
 * Get supported currencies
 * @returns {Array} Array of supported currency objects
 */
export const getSupportedCurrencies = () => [
  { code: 'THB', name: 'Thai Baht', symbol: '฿' },
  { code: 'USD', name: 'US Dollar', symbol: '$' }
];

/**
 * Get default currency
 * @returns {string} Default currency code
 */
export const getDefaultCurrency = () => 'THB';
